import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GlobalSettings {
  maintenance_mode: boolean;
  phone_auth_enabled: boolean;
  otp_provider: 'fast2sms' | 'msg91' | 'other';
}

const defaultSettings: GlobalSettings = {
  maintenance_mode: false,
  phone_auth_enabled: false,
  otp_provider: 'fast2sms',
};

// Global cache for 0-lag instant loads
let _cachedSettings: GlobalSettings | null = null;
const listeners = new Set<(settings: GlobalSettings) => void>();

// Notify all mounted hooks when settings change
const notifyListeners = (settings: GlobalSettings) => {
  listeners.forEach((listener) => listener(settings));
};

export const useGlobalSettings = () => {
  const [settings, setSettings] = useState<GlobalSettings>(_cachedSettings || defaultSettings);
  const [loading, setLoading] = useState(_cachedSettings === null);

  useEffect(() => {
    listeners.add(setSettings);

    // Load from localStorage if available for 0-lag instant UI
    const saved = localStorage.getItem('app_settings_cache');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        _cachedSettings = parsed;
        setSettings(parsed);
        setLoading(false);
      } catch (e) {
        console.error('Error parsing settings cache', e);
      }
    }

    // Always fetch in background to stay fresh (Stale-While-Revalidate)
    supabase
      .from('app_settings')
      .select('key, value')
      .in('key', ['maintenance_mode', 'phone_auth_enabled', 'otp_provider'])
      .then(({ data, error }) => {
        if (!error && data) {
          const newSettings = { ...defaultSettings };
          data.forEach((item) => {
            if (item.key === 'maintenance_mode') newSettings.maintenance_mode = item.value === 'true';
            if (item.key === 'phone_auth_enabled') newSettings.phone_auth_enabled = item.value === 'true';
            if (item.key === 'otp_provider') newSettings.otp_provider = (item.value as any) || 'fast2sms';
          });
          
          // Only update and notify if settings actually changed to avoid unnecessary re-renders
          if (JSON.stringify(newSettings) !== JSON.stringify(_cachedSettings)) {
            _cachedSettings = newSettings;
            localStorage.setItem('app_settings_cache', JSON.stringify(newSettings));
            notifyListeners(newSettings);
          }
          setLoading(false);
        } else {
           setLoading(false);
        }
      });

    return () => {
      listeners.delete(setSettings);
    };
  }, []);

  return { settings, loading };
};

// Drop-in replacement for the old maintenance hook to keep existing components happy
export const useMaintenanceMode = () => {
  const { settings, loading } = useGlobalSettings();
  return { isMaintenanceMode: settings.maintenance_mode, loading };
};
